<script setup lang="ts">
import type { SiteNewsCard } from '~/types/types'

/**
 * News teaser card. `featured` renders the large hero variant used at the
 * top of the news hub.
 */
const props = withDefaults(defineProps<{
  post: SiteNewsCard
  featured?: boolean
}>(), { featured: false })

const { t } = useI18n()
const localePath = useLocalePath()
const siteDate = useSiteDate()

const dateLabel = computed(() => siteDate.short(props.post.published_at))
</script>

<template>
  <NuxtLink
    :to="localePath(`/news/${post.slug}`)"
    class="site-surface-card group block overflow-hidden transition hover:-translate-y-0.5"
    :class="featured ? 'sm:grid sm:grid-cols-2' : ''"
  >
    <div
      v-if="post.cover_image_url"
      class="overflow-hidden"
      :class="featured ? 'aspect-[16/10] sm:aspect-auto sm:h-full' : 'aspect-[16/9]'"
    >
      <img
        :src="post.cover_image_url"
        :alt="post.title"
        class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        loading="lazy"
      >
    </div>
    <div :class="featured ? 'p-6 sm:p-8' : 'p-5'">
      <div class="flex items-center gap-2 text-xs text-[var(--site-muted)]">
        <span
          v-if="post.category"
          class="site-bg-accent-soft site-text-accent rounded-full px-2 py-0.5 font-medium"
        >{{ post.category.name }}</span>
        <span v-if="dateLabel">{{ dateLabel }}</span>
        <span
          v-if="post.view_count"
          class="inline-flex items-center gap-1"
        >
          <UIcon
            name="i-lucide-eye"
            class="size-3"
          />{{ post.view_count }}
        </span>
      </div>
      <h3
        class="site-display mt-2 tracking-tight"
        :class="featured ? 'text-2xl sm:text-3xl' : 'text-lg'"
      >
        {{ post.title }}
      </h3>
      <p
        v-if="post.excerpt"
        class="mt-2 text-sm leading-relaxed text-[var(--site-muted)]"
        :class="featured ? 'sm:text-base' : 'line-clamp-2'"
      >
        {{ post.excerpt }}
      </p>
      <span class="site-text-accent mt-3 inline-block text-sm font-medium">
        {{ t('site.read_more') }} →
      </span>
    </div>
  </NuxtLink>
</template>
